import request from './axios';
import type {
  ScheduleInfo,
  CopyScheduleResponse,
  QueryScheduleParams,
  CreateScheduleParams,
  UpdateScheduleParams,
  SlotInfo,
  QuerySlotParams,
  CreateSlotParams,
  UpdateSlotParams,
  CalDateInfo,
  UpsertCalDateParams,
} from '../types/schedule.types';
import { getCurrentUserID, getCurrentUserOrgID } from '../utils/userUtils';

export const getScheduleList = (params?: QueryScheduleParams) => {
  return request.get<ScheduleInfo[]>('/api/schedule', { params });
};

export const getScheduleById = (scheduleId: string) => {
  return request.get<ScheduleInfo>(`/api/schedule/${scheduleId}`, { timeout: 30000 });
};

export const getSchedulesByBreakdownId = (breakdownId: string) => {
  return request.get<ScheduleInfo[]>(`/api/schedule/breakdown/${breakdownId}`, { timeout: 30000 });
};

export const getSchedulesByFilmId = (filmId: string) => {
  return request.get<ScheduleInfo[]>(`/api/schedule/film/${filmId}`);
};

export const createSchedule = (data: CreateScheduleParams) => {
  return request.post<ScheduleInfo>('/api/schedule', data);
};

export const updateSchedule = (scheduleId: string, data: UpdateScheduleParams) => {
  return request.patch<ScheduleInfo>(`/api/schedule/${scheduleId}`, data);
};

export const deleteSchedule = (scheduleId: string) => {
  return request.delete(`/api/schedule/${scheduleId}`);
};

export const getSlotList = (params?: QuerySlotParams) => {
  return request.get<SlotInfo[]>('/api/slot', { params });
};

export const getSlotById = (slotId: string) => {
  return request.get<SlotInfo>(`/api/slot/${slotId}`);
};

export const getSlotsBySceneId = (sceneId: string) => {
  return request.get<SlotInfo[]>(`/api/slot/scene/${sceneId}`);
};

export const getSlotsByScheduleId = (scheduleId: string) => {
  return request.get<SlotInfo[]>(`/api/slot/schedule/${scheduleId}`, { timeout: 30000 });
};

export const createSlot = (data: CreateSlotParams) => {
  return request.post<SlotInfo>('/api/slot', data);
};

export const createSlotBatch = (data: { slots: CreateSlotParams[] }) => {
  return request.post<SlotInfo[]>('/api/slot/batch', data);
};

export const updateSlot = (slotId: string, data: UpdateSlotParams) => {
  return request.patch<SlotInfo>(`/api/slot/${slotId}`, data);
};

export const deleteSlot = (slotId: string) => {
  return request.delete(`/api/slot/${slotId}`);
};

export const getCalDatesByScheduleId = (scheduleId: string) => {
  return request.get<CalDateInfo[]>(`/api/calDate/schedule/${scheduleId}`, { timeout: 30000 });
};

export const upsertCalDate = (data: UpsertCalDateParams) => {
  return request.put<CalDateInfo>('/api/calDate', data);
};

export const upsertCalDateBatch = (data: { calDates: UpsertCalDateParams[] }) => {
  return request.put<CalDateInfo[]>('/api/calDate/batch', data);
};

export const updateCalDateSoltOrder = (
  calDateID: string,
  slotID: string,
  newIndex: number,
) => {
  return request.patch<CalDateInfo>(`/api/calDate/${calDateID}/slot-order`, {
    slotID,
    newIndex,
  });
};

export const updateCalDateSlotsSort = (calDateID: string, calDateSlotsSort: string[]) => {
  return request.patch<CalDateInfo>(`/api/calDate/${calDateID}/slots-sort`, {
    calDateSlotsSort,
  });
};

export const updateSlotsDate = (slotIDs: string[], date: string) => {
  return request.patch('/api/slot/batch-update-date', { slotIDs, date });
};

export const shiftSlotsDate = (slotIDs: string[], daysOffset: number) => {
  return request.patch<SlotInfo[]>('/api/slot/batch-shift-date', { slotIDs, daysOffset });
};

export type BatchSlotLocationVisibilityItem = {
  slotID: string;
  slotLocationVisibility: 'Y' | 'N';
  slotShift?: SlotInfo['slotShift'];
};

export const batchPatchSlotLocationVisibility = (data: {
  items: BatchSlotLocationVisibilityItem[];
}) => {
  return request.patch<SlotInfo[]>('/api/slot/batch-location-visibility', data);
};

export type MoveSlotsParams = {
  scheduleID: string;
  sourceDate: string;
  targetDate: string;
  updates: Array<{
    slotID: string;
    slotLocationVisibility?: 'Y' | 'N';
    slotShift?: SlotInfo['slotShift'];
  }>;
  moveSlotIDs: string[];
  deleteSlotIDs: string[];
  sourceSort: string[];
  targetSort: string[];
  targetShift?: string;
  expectedSourceUpdateTime?: string;
  expectedTargetUpdateTime?: string;
};

export const moveSlots = (data: MoveSlotsParams) => {
  const toSecondPrecision = (value?: string) =>
    value ? new Date(Math.floor(new Date(value).getTime() / 1000) * 1000).toISOString() : undefined;
  return request.patch<{ slots: SlotInfo[]; calDates: CalDateInfo[] }>('/api/calDate/move-slots', {
    ...data,
    expectedSourceUpdateTime: toSecondPrecision(data.expectedSourceUpdateTime),
    expectedTargetUpdateTime: toSecondPrecision(data.expectedTargetUpdateTime),
  });
};

export const shiftCalDatesDate = (
  calDateIDs: string[],
  daysOffset: number,
  originalStartDate?: string | Date | null,
) => {
  const payload: { calDateIDs: string[]; daysOffset: number; originalStartDate?: string } = {
    calDateIDs,
    daysOffset,
  };

  if (originalStartDate) {
    if (originalStartDate instanceof Date) {
      const year = originalStartDate.getFullYear();
      const month = String(originalStartDate.getMonth() + 1).padStart(2, '0');
      const day = String(originalStartDate.getDate()).padStart(2, '0');
      payload.originalStartDate = `${year}-${month}-${day}`;
    } else {
      payload.originalStartDate = originalStartDate;
    }
  }

  return request.patch<CalDateInfo[]>('/api/calDate/batch-shift-date', payload);
};

export const deleteCalDate = (calDateId: string) => {
  return request.delete(`/api/calDate/${calDateId}`);
};

export const copySchedule = (data: { sourceScheduleID: string; targetScheduleName?: string }) => {
  try {
    const userID = getCurrentUserID();
    const userOrgID = getCurrentUserOrgID();

    if (userID && userOrgID) {
      return request.post<CopyScheduleResponse>('/api/schedule/copy', data, {
        headers: {
          'X-User-Id': userID,
          'X-User-Org-Id': userOrgID,
        },
      });
    }
    throw new Error('用户信息不完整：缺少用户ID或组织ID');
  } catch (error) {
    console.error('获取用户信息失败:', error);
    return request.post<CopyScheduleResponse>('/api/schedule/copy', data);
  }
};
